import type { operations } from "@airange/generated-contracts/edu";
import { eduAdminOpenApiClient } from "../generated/client";
import { omitEmptyQuery } from "./openapi-query";
import { unwrapOpenApiResult } from "./openapi-result";

type GetOrgsOverviewOperation = operations["get_orgs_overview_api_orgs_overview_get"];
type GetOrgsOperation = operations["get_orgs_api_orgs_get"];
type GetOrgOperation = operations["get_org_api_orgs__org_id__get"];
type GetOrgsGeneratedQuery = NonNullable<GetOrgsOperation["parameters"]["query"]>;
type OrgId = GetOrgOperation["parameters"]["path"]["org_id"];
type UiNonNullQuery<TQuery extends object, TReplaced extends keyof TQuery = never> = {
  [TKey in Exclude<keyof TQuery, TReplaced>]?: NonNullable<TQuery[TKey]>;
};

export type EduOrgsOverview = GetOrgsOverviewOperation["responses"][200]["content"]["application/json"];
export type EduOrgListQuery = UiNonNullQuery<GetOrgsGeneratedQuery, "page_size"> & {
  pageSize?: NonNullable<GetOrgsGeneratedQuery["page_size"]>;
};
export type EduOrgsListResponse = GetOrgsOperation["responses"][200]["content"]["application/json"];
export type EduOrgListItem = EduOrgsListResponse["items"][number];
export type EduOrgDetail = GetOrgOperation["responses"][200]["content"]["application/json"];

function toOrgListQueryParams(query?: EduOrgListQuery): GetOrgsGeneratedQuery | undefined {
  if (!query) return undefined;
  const { pageSize, ...generatedQuery } = query;
  return omitEmptyQuery<GetOrgsGeneratedQuery>({
    ...generatedQuery,
    ...(pageSize === undefined ? {} : { page_size: pageSize }),
  });
}

const eduOrgsApi = {
  async getOrgsOverview(): Promise<EduOrgsOverview> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/orgs/overview"));
  },
  async getOrgs(query?: EduOrgListQuery): Promise<EduOrgsListResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/orgs", {
        params: { query: toOrgListQueryParams(query) },
      }),
    );
  },
  async getOrgDetail(orgId: OrgId): Promise<EduOrgDetail> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/orgs/{org_id}", {
        params: { path: { org_id: orgId } },
      }),
    );
  },
};

export default eduOrgsApi;
